import React, { useEffect, useMemo, useState } from "react";
import { Card, message } from "antd";
import { Check, Clock3, MoveLeft, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import {
  deleteSocialListeningAnalysis,
  fetchSocialListeningHistory,
  SocialListeningHistoryItem,
} from "src/services/SocialListeningAPIs";
import "./SocialListingAgent.scss";
import CircularLoader from "src/components/reusable/CircularLoader/CircularLoader";
import { formatNumber, getNumberLocale } from "src/utils/numberFormat";
import { getDemoSocialListeningHistory, isDemoAnalysisId } from "./demoSocialListeningReports";

const History: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [historyItems, setHistoryItems] = useState<SocialListeningHistoryItem[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [dismissedDemoIds, setDismissedDemoIds] = useState<number[]>([]);
  const numberLocale = useMemo(
    () => getNumberLocale(i18n.language),
    [i18n.language]
  );

  useEffect(() => {
    const userIdRaw = localStorage.getItem("user_id");
    const userId = Number(userIdRaw);
    if (userIdRaw === null || Number.isNaN(userId)) {return;}

    fetchSocialListeningHistory(userId)
      .then(items => setHistoryItems(items || []))
      .catch(error => {
        console.error("Social listening history error:", error);
      });
  }, []);

  const demoHistory = useMemo(
    () =>
      getDemoSocialListeningHistory(i18n.language).filter(
        item => !dismissedDemoIds.includes(item.analysis_id ?? NaN)
      ),
    [i18n.language, dismissedDemoIds]
  );

  const rows = useMemo(() => {
    const merged = [...demoHistory, ...historyItems];
    return merged.sort((a, b) => {
      const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [demoHistory, historyItems]);
  const selectableIds = useMemo(
    () =>
      rows
        .map(row => Number(row?.analysis_id ?? row?.id))
        .filter(id => Number.isFinite(id)),
    [rows]
  );

  const isAllSelected =
    selectableIds.length > 0 && selectableIds.every(id => selectedIds.includes(id));

  const getAnalysisLabel = (item: SocialListeningHistoryItem) => {
    const rawType =
      item?.analysis_type ??
      (item as any)?.analysisType ??
      (item as any)?.analysis_type_name ??
      (item as any)?.analysis_kind ??
      (item as any)?.type ??
      (item as any)?.analysis?.analysis_type ??
      (item as any)?.analysis?.analysisType ??
      (item as any)?.analysis?.type ??
      (item as any)?.meta?.analysis_type ??
      (item as any)?.metadata?.analysis_type ??
      "";

    const normalizedType = String(rawType).toLowerCase();
    if (normalizedType.includes("competitive") || normalizedType.includes("competitor")) {
      return t("socialListingAgent.historyPage.analysisTypeCompetitor");
    }
    if (normalizedType.includes("brand")) {
      return t("socialListingAgent.historyPage.analysisTypeBrand");
    }

    const title = String(item?.report_title ?? "").toLowerCase();
    if (title.includes("competitive") || title.includes("competitor")) {
      return t("socialListingAgent.historyPage.analysisTypeCompetitor");
    }
    if (title.includes("brand")) {
      return t("socialListingAgent.historyPage.analysisTypeBrand");
    }

    return t("socialListingAgent.historyPage.analysisTypeBrand");
  };

  const getAuthorName = (item: SocialListeningHistoryItem) => {
    const rawAuthor =
      item?.author_name ??
      item?.author ??
      item?.created_by_name ??
      item?.created_by ??
      item?.user_name ??
      item?.username ??
      item?.full_name ??
      item?.name;

    const author = String(rawAuthor ?? "").trim();
    return author || t("socialListingAgent.historyPage.unknownAuthor");
  };

  const getAnalysisState = (item: SocialListeningHistoryItem): "processing" | "completed" => {
    const rawStatus =
      item?.status ??
      item?.analysis_status ??
      item?.state ??
      item?.processing_status ??
      item?.analysis?.status ??
      "";
    const normalizedStatus = String(rawStatus).toLowerCase();

    const isProcessing =
      normalizedStatus.includes("progress") ||
      normalizedStatus.includes("pending") ||
      normalizedStatus.includes("process") ||
      normalizedStatus.includes("running") ||
      normalizedStatus.includes("queue") ||
      normalizedStatus.includes("incomplete") ||
      item?.is_processing === true ||
      item?.processing === true ||
      item?.is_completed === false;

    if (isProcessing) {return "processing";}
    return "completed";
  };

  const formatHistoryDateTime = (value: string | null) => {
    if (!value) {return "";}
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {return "";}

    const datePart = date.toLocaleDateString(numberLocale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const timePart = date.toLocaleTimeString(numberLocale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return `${datePart} ${t("socialListingAgent.historyPage.at")} ${timePart}`;
  };

  return (
    <section className="history-page">
      <div className="history-header">
        <button
          className="history-back-button"
          type="button"
          onClick={() => navigate("/account/social-listing-agent")}
        >
          <MoveLeft className="Brand-back-button" />
          {t("socialListingAgent.historyPage.back")}
        </button>
        <h1 className="history-heading">{t("socialListingAgent.historyPage.title")}</h1>
      </div>

      <Card className="history-card" bordered={false}>
        <div className="history-card-header">
          <div className="history-header-select">
            <input
              className="history-checkbox"
              type="checkbox"
              checked={isAllSelected}
              onChange={event => {
                setSelectedIds(event.target.checked ? selectableIds : []);
              }}
            />
          </div>

          <div className="history-actions">
            <Tippy content={t("socialListingAgent.historyPage.deleteTooltip")} placement="top">
              <span className="history-icon-tooltip-trigger">
                <button
                  className="history-icon-button"
                  type="button"
                  aria-label={t("socialListingAgent.historyPage.deleteSelectedAria")}
                  disabled={selectedIds.length === 0 || isDeleting}
                  onClick={async () => {
                    const userIdRaw = localStorage.getItem("user_id");
                    const userId = Number(userIdRaw);
                    if (userIdRaw === null || Number.isNaN(userId)) {return;}
                    if (selectedIds.length === 0) {return;}

                    try {
                      setIsDeleting(true);

                      const realIds = selectedIds.filter(id => !isDemoAnalysisId(id));
                      await Promise.all(
                        realIds.map(analysisId =>
                          deleteSocialListeningAnalysis(userId, analysisId)
                        )
                      );
                      setHistoryItems(prev =>
                        prev.filter(row => {
                          const rowId = Number(row?.analysis_id ?? row?.id);
                          return !selectedIds.includes(rowId);
                        })
                      );
                      setDismissedDemoIds(prev => [
                        ...prev,
                        ...selectedIds.filter(isDemoAnalysisId),
                      ]);
                      setSelectedIds([]);
                      message.success(t("socialListingAgent.historyPage.deleteSuccess"));
                    } catch (error) {
                      console.error("Delete analysis failed:", error);
                      message.error(t("socialListingAgent.historyPage.deleteFailed"));
                    } finally {
                      setIsDeleting(false);
                    }
                  }}
                >
                  <Trash2 size={20} className="history-delete-icon" />
                </button>
              </span>
            </Tippy>

            <Tippy content={t("socialListingAgent.historyPage.downloadTooltip")} placement="top">
              <button
                className="history-icon-button"
                type="button"
                aria-label={t("socialListingAgent.historyPage.downloadAria")}
                onClick={async () => {
                  if (selectedIds.length === 0) {
                    message.warning(t("socialListingAgent.historyPage.downloadSelectWarning"));
                    return;
                  }
                  selectedIds.forEach(analysisId => {
                    window.open(
                      `/account/social-listing-agent/${analysisId}?download=1`,
                      "_blank"
                    );
                  });
                }}
              >
                <img src="/Icons/Downloads.svg" className="report-icon" />
              </button>
            </Tippy>
          </div>
        </div>

        <div className="history-list">
          {rows.length === 0 ? (
            <div className="recent-report-empty">
              {t("socialListingAgent.historyPage.noReports")}
            </div>
          ) : (
            rows.map((item, index) => {
              const title =
                item?.report_title ||
                item?.company_name ||
                t("socialListingAgent.historyPage.reportFallback", {
                  index: formatNumber(index + 1, {}, i18n.language),
                });

              const dateValue = item?.updated_at || item?.created_at || null;
              const formattedDate = formatHistoryDateTime(dateValue);

              const analysisType = getAnalysisLabel(item);
              const authorName = getAuthorName(item);
              const analysisState = getAnalysisState(item);
              const analysisId = Number(item?.analysis_id ?? item?.id);
              const isValidId = Number.isFinite(analysisId);

              return (
                <div
                  key={item?.analysis_id ?? item?.id ?? `${title}-${index}`}
                  className="history-row"
                >
                  <input
                    className="history-checkbox"
                    type="checkbox"
                    checked={isValidId ? selectedIds.includes(analysisId) : false}
                    onChange={event => {
                      if (!isValidId) {return;}
                      setSelectedIds(prev =>
                        event.target.checked
                          ? [...prev, analysisId]
                          : prev.filter(id => id !== analysisId)
                      );
                    }}
                  />
                  <div>
                    <div className="history-title-row">
                      <span className="history-badge">{analysisType}</span>
                    </div>
                    <div className="history-item-title-row">
                      <span className="history-item-title">{title}</span>
                      {analysisState === "completed" && (
                        <span
                          className="history-status-success"
                          aria-label={t("socialListingAgent.historyPage.completed")}
                        >
                          <Check size={12} />
                        </span>
                      )}
                    </div>
                    <div className="history-author">
                      {t("socialListingAgent.historyPage.byAuthor", { author: authorName })}
                    </div>
                    {formattedDate && (
                      <div className="history-date">
                        <Clock3 size={12} />
                        <span>{formattedDate}</span>
                      </div>
                    )}
                  </div>

                  <div className="history-status-cell">
                    {analysisState === "processing" && (
                      <CircularLoader
                        size={34}
                        thickness={5}
                        color="#36b25b"
                        trackColor="#d9d9d9"
                      />
                    )}
                  </div>

                  <button
                    type="button"
                    className="button button--primary history-view-button"
                    disabled={!isValidId}
                    onClick={() => {
                      if (!isValidId) {return;}
                      navigate(`/account/social-listing-agent/${analysisId}`);
                    }}
                  >
                    {t("socialListingAgent.historyPage.viewReport")}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </Card>
    </section>
  );
};

export default History;
